export type InvoicePresentation = {
  label: string;
  estimateDisplayNumber?: string | null;
  soldLater: boolean;
  paidCents: number;
  isNoCharge: boolean;
  isPaid: boolean;
  isPaidReceipt: boolean;
};

export type InvoicePaymentBadge =
  | { kind: "paid"; amountCents: number }
  | { kind: "partially_paid"; amountCents: number }
  | { kind: "unpaid"; amountCents: number }
  | { kind: "void" }
  | { kind: "no_charge" };

/**
 * Database statuses describe the accounting lifecycle. This translates them
 * into the operational language people need when looking at sold work.
 */
export function invoicePresentation(input: {
  status: string;
  totalCents: number;
  balanceDueCents: number;
  paidCents?: number | null;
  jobStatus?: string | null;
  fulfillment?: string | null;
  estimateDisplayNumber?: string | null;
  hasInvoiceSignature?: boolean | null;
  signerName?: string | null;
}): InvoicePresentation {
  const paidCents = Math.max(0, input.paidCents ?? input.totalCents - input.balanceDueCents);
  const soldLater = input.fulfillment === "later" && input.jobStatus === "new";
  const isNoCharge = input.status !== "draft" && input.status !== "void" && input.totalCents === 0 && paidCents === 0;
  const isPaid = input.totalCents > 0 && input.balanceDueCents <= 0;
  let label: string;
  if (input.status === "void") label = "Void";
  else if (isNoCharge) label = "No charge";
  else if (isPaid) label = "Paid";
  else if (input.status === "partially_paid" || (paidCents > 0 && input.balanceDueCents > 0)) label = "Partially paid — balance due";
  // A signed invoice is ready to collect even when its accounting status remains
  // draft for compatibility. Do not let the fulfillment label hide that payment
  // is now outstanding.
  else if (input.status === "sent" || input.hasInvoiceSignature || Boolean(input.signerName)) label = "Balance due";
  else if (soldLater) label = "Sold / Unscheduled — deposit available";
  else if (input.fulfillment === "now" || input.estimateDisplayNumber) label = "Open — work in progress";
  else label = "Invoice in progress";
  return {
    label,
    estimateDisplayNumber: input.estimateDisplayNumber,
    soldLater,
    paidCents,
    isNoCharge,
    isPaid,
    isPaidReceipt: isPaid && paidCents > 0,
  };
}

/** The compact badge answers whether the customer owes money and how much. */
export function invoicePaymentBadge(input: {
  status: string;
  totalCents: number;
  balanceDueCents: number;
  paidCents?: number | null;
}): InvoicePaymentBadge | null {
  const presentation = invoicePresentation(input);
  if (input.status === "void") return { kind: "void" };
  if (presentation.isNoCharge) return { kind: "no_charge" };
  if (presentation.isPaid) return { kind: "paid", amountCents: input.totalCents };
  if (presentation.paidCents > 0 && input.balanceDueCents > 0)
    return { kind: "partially_paid", amountCents: input.balanceDueCents };
  if (input.balanceDueCents > 0) return { kind: "unpaid", amountCents: input.balanceDueCents };
  return null;
}

export function estimatePresentation(input: {
  status: string;
  optionsManaged?: number | boolean;
  pendingApprovalCount?: number;
  approvalCount?: number;
}) {
  if (input.optionsManaged && (input.pendingApprovalCount ?? 0) > 0) return "Approved — job needed";
  if (input.optionsManaged && (input.approvalCount ?? 0) > 0) return "Approved";
  if (input.status === "draft") return "Draft estimate";
  if (input.status === "sent") return "Awaiting customer decision";
  return input.status.replaceAll("_", " ");
}

export function jobPresentation(status: string, invoices: Array<{ fulfillment?: string | null }> = []) {
  return status === "new" && invoices.some((invoice) => invoice.fulfillment === "later")
    ? "Sold / Unscheduled"
    : status;
}
